/**
 * The Season Report — `DESIGN-GOVERNORS.md` §12, *Rhythm*:
 *
 * > **Season Report** — at each settlement, one card: what produced, what
 * > settled, who moved, what's due next.
 *
 * Four questions, four blocks, in that order and under those names. Everything
 * below is the arithmetic that answers them and nothing else: this module has no
 * state, no clock, no DOM and no simulation. It takes the season that just closed,
 * the one before it, and returns a document.
 *
 * ## It is a pure function of the Chronicle, and that is the whole design
 *
 * `buildSeasonReport(record, previous?)` is handed two `SeasonRecord`s. Not a
 * `SurfaceSim`, not a manager, not a district — two lines of the public record,
 * which is the same document Phase 3 promotes to the world's (§16).
 *
 * Three things follow from that signature, and each of them is the reason for it:
 *
 * - **Nothing on the card can be a quantity the world server will not publish.**
 *   A figure a mayor can read here is a figure their governor can read there. A
 *   card that reached into a live manager could show something the Chronicle does
 *   not carry, and the first time a player noticed the difference the record would
 *   be the thing they stopped trusting.
 * - **Nothing on the card can be an authored delta** (Law 1). Every number is
 *   either a level the record holds or a subtraction over two records, and
 *   `seasonFlows` does most of the subtracting. There is no third kind of number
 *   in this file.
 * - **Nothing on the card can be about a colony** (Law 7). Not by discipline —
 *   `SeasonRecord` structurally has no ant in it, because `SeasonManager` cannot
 *   reach one. The card inherits the guarantee from its input type rather than
 *   restating it, and `tests/season-report.test.ts` holds this file to the
 *   settler's own standard anyway: one import, and a vocabulary check.
 *
 * ## What is absent rather than zero
 *
 * The record omits a whole block for a district that has no federal estate, no
 * federal jobs, nothing wrong with any household, no works, no bill and no
 * corridor. The card follows it line by line: a district that has never been
 * tithed has no tithe row, and one that has never lost a family has no departure
 * rows. A first season in an untouched meadow is four short blocks; a district
 * with a slab, a strike, a drought and a corridor on it is a long card.
 *
 * So **the report is exactly as complicated as the district is**, which is the
 * rule `SeasonRecord`, `PowerDemand` and the status panel all already follow, and
 * the reason none of them ever shows a row of zeroes to somebody who has not yet
 * met the mechanic.
 *
 * ## What it does not say
 *
 * No verdict, no rating, no trend arrow, no target. The blocks are counted
 * quantities in the flat municipal register the status panel has always used, and
 * the two numbers a reader would most want netted against each other — a toll in
 * dollars and road wear in points of quality — are deliberately left in different
 * units and different rows, exactly as the Chronicle leaves them.
 *
 * The footer is the one line on the card with a voice, and what it says is that
 * no action is required.
 *
 * See `docs/seasons.md`.
 */

import {
  CYCLES_PER_SEASON,
  DEPARTURE_CAUSE_ORDER,
  type SeasonRecord,
  seasonFlows,
} from '@antification/core';

/**
 * How a value is coloured, and the palette is the status panel's.
 *
 * Three tones, because the panel has three: plain municipal grey for a counted
 * quantity, and the budget's existing green and red for a treasury that is up or
 * down. Nothing else on the card is coloured — a departure is grey, a strike is
 * grey, and a season in which the meadow halved is grey, because the panel has
 * never known how to feel about any of them.
 */
export type ReportTone = 'plain' | 'credit' | 'debit';

/** One row: what it is, what it came to, and sometimes its other half. */
export interface ReportLine {
  label: string;
  value: string;
  /**
   * The figure's counterpart, in parentheses and in the same grey.
   *
   * Always another measured quantity — `1,240 refused` beside what was
   * delivered, `$180 unfunded` beside what the roads were paid — never a
   * comment on the one in front of it.
   */
  note?: string;
  tone?: ReportTone;
}

/** One of §12's four questions, with the rows that answer it. */
export interface ReportBlock {
  title: string;
  lines: ReportLine[];
}

/** The card. */
export interface SeasonReport {
  /** The season that closed. */
  season: number;
  /** `Cycles 91–180` — the span of budget cycles this card is about. */
  span: string;
  /** Always four, and always in §12's order. A block with no rows is dropped. */
  blocks: ReportBlock[];
  /** The municipal cheer. The one line here that is not a number. */
  footer: string;
}

/** §12's four questions, in §12's order. */
const PRODUCED = 'WHAT PRODUCED';
const SETTLED = 'WHAT SETTLED';
const MOVED = 'WHO MOVED';
const DUE = "WHAT'S DUE NEXT";

/**
 * What a household's stated reason is called on a civic document.
 *
 * `DEPARTURE_CAUSE_ORDER` is the simulation's vocabulary and these are the
 * mayor's. Each one is the term that took most off the score, said plainly and
 * with no adjective: the district is reporting what it measured, and a card that
 * said *lost to preventable brownouts* would be the interface arguing a case the
 * Chronicle does not make.
 */
const DEPARTURE_LABELS: Readonly<Record<string, string>> = {
  insolvency: 'Left with nothing',
  brownout: 'Left over the dark',
  dryTaps: 'Left over dry taps',
  sealedGround: 'Left over sealed ground',
  badRoads: 'Left over the roads',
};

/** `1,240`. Every count on the card goes through here. */
function count(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/** `$1,240`, and `-$45` when the district is under. */
function money(n: number): string {
  const whole = Math.round(n);
  return whole < 0 ? `-$${count(-whole)}` : `$${count(whole)}`;
}

/** `+$312` / `-$45`. For a change in a level, where the sign is the point. */
function signedMoney(n: number): string {
  const whole = Math.round(n);
  return `${whole < 0 ? '-' : '+'}$${count(Math.abs(whole))}`;
}

/** `+37` / `-12` / `0`. */
function signedCount(n: number): string {
  const whole = Math.round(n);
  if (whole === 0) return '0';
  return `${whole < 0 ? '-' : '+'}${count(Math.abs(whole))}`;
}

/** `78%`. A ratio over two measured sums, and `—` when the denominator is nothing. */
function ratio(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

/**
 * Whether a figure survives being written down.
 *
 * Every value on this card is rounded before it is printed, so the test for
 * *is there anything to say* has to be the rounded one. `> 0` on the raw number
 * puts `Dividends $0` and `Tithe remitted $0` on a card — measured against a
 * season in which a district distributed forty cents — and a row of zeroes is
 * exactly what `SeasonRecord`'s absent blocks exist to keep off a document.
 */
function shown(n: number): boolean {
  return Math.round(n) !== 0;
}

/** A dollar row, and nothing at all when the dollars round to none. */
function pushMoney(
  lines: ReportLine[],
  label: string,
  amount: number,
  extra: Omit<ReportLine, 'label' | 'value'> = {}
): void {
  if (!shown(amount)) return;
  lines.push({ label, value: money(amount), ...extra });
}

/**
 * The season that closed, as a card.
 *
 * `previous` is omitted for a district's first season, where `seasonFlows`
 * reports the world's own starting values: the totals all begin at zero and the
 * two levels that do not (`pool`, `roadQuality`) are levels either way.
 */
export function buildSeasonReport(
  record: SeasonRecord,
  previous?: SeasonRecord
): SeasonReport {
  const flows = seasonFlows(record, previous);
  const from = record.cycles - flows.cycles + 1;

  const blocks = [
    { title: PRODUCED, lines: produced(record, flows, previous !== undefined) },
    { title: SETTLED, lines: settled(record, flows) },
    { title: MOVED, lines: moved(record, previous, flows) },
    { title: DUE, lines: due(record) },
  ].filter((block) => block.lines.length > 0);

  return {
    season: record.season,
    span: `Cycles ${count(from)}–${count(record.cycles)}`,
    blocks,
    // §12 keeps Parkington's municipal cheer on the chrome, and this is the
    // whole of it. The card has just reported a season in which families left
    // over the dark and a slab took twenty-four cells, and the district's own
    // stationery signs off by saying there is nothing to do about any of it.
    footer: 'PARKINGTON CIVIC SUITE™ · Filed automatically. No action required.',
  };
}

type Flows = ReturnType<typeof seasonFlows>;

/**
 * What produced — the physical season.
 *
 * §4's production step, and since ADR-025 it has something in it: what the ground
 * became, what the district drew, and what the federal estate made out of it. The
 * two rows about the ground are unconditional because they are the district's own
 * subject; everything under them appears once the district has the mechanic.
 */
function produced(record: SeasonRecord, flows: Flows, comparable: boolean): ReportLine[] {
  const lines: ReportLine[] = [];

  lines.push({
    label: 'Ground developed',
    value: `${signedCount(flows.cellsPaved)} cells`,
    // The meadow, in the same row and in the opposite direction. The card does
    // not say the second number is a consequence of the first, because in this
    // district it is only usually one.
    //
    // **Only when there is a season to compare against.** `seasonFlows` subtracts
    // against zero for a district's first record, which is right for every total
    // and for the three levels that genuinely start at zero — pavement, families,
    // buildings — and wrong for this one: a meadow starts full. Season 1's
    // `bushChange` is the standing bush count wearing a plus sign, so the card
    // that would have read *meadow +226* says nothing instead. The district has
    // no earlier measurement, and neither does the Chronicle.
    note:
      comparable && shown(flows.bushChange)
        ? `meadow ${signedCount(flows.bushChange)}`
        : undefined,
  });

  lines.push({
    label: 'Structures',
    value: signedCount(flows.buildingChange),
    note: `${count(record.town.built)} standing`,
  });

  if (shown(flows.deliveredKwCycles) || shown(flows.shedKwCycles)) {
    lines.push({
      label: 'Power delivered',
      value: `${count(flows.deliveredKwCycles)} kW-cy`,
      note: shown(flows.shedKwCycles) ? `${count(flows.shedKwCycles)} refused` : undefined,
    });
  }

  if (shown(flows.deliveredM3Cycles) || shown(flows.cutM3Cycles)) {
    lines.push({
      label: 'Water delivered',
      value: `${count(flows.deliveredM3Cycles)} m³-cy`,
      note: shown(flows.cutM3Cycles) ? `${count(flows.cutM3Cycles)} refused` : undefined,
    });
  }

  // The rainfall, and only when it was not a normal year. It is the one figure
  // in the whole record that is a fact about the world rather than about the
  // district, and a card that printed `100%` every season would bury the season
  // it did not.
  if (record.water.rainfall !== 1) {
    lines.push({
      label: 'Rainfall',
      value: `${Math.round(record.water.rainfall * 100)}% of normal`,
    });
  }

  const estate = record.facilities;
  if (estate) {
    lines.push({
      label: 'Federal estate',
      value: `${ratio(flows.facilityUptimeCycles, flows.facilityMeteredCycles)} supplied`,
      note: `${count(estate.count)} standing · ${count(estate.sealedCells)} cells sealed`,
    });
  }

  // What the estate made, and — deliberately — not what it fetched. §4's rule is
  // `min(power, water, staffing) × capacity`, so this row is the row above it
  // multiplied, and a mayor comparing the two is reading the whole of what
  // curtailing a facility does. The **sale** is not on this card at all: it is the
  // polity's money, the card is the district's own publication, and a civic
  // record that reported somebody else's revenue would be reporting a number the
  // district has no claim on and no way to check.
  if (shown(flows.computeProduced)) {
    lines.push({
      label: 'Compute produced',
      value: `${count(flows.computeProduced)} units`,
    });
  }

  const labour = record.labour;
  if (labour && labour.posts > 0) {
    lines.push({
      label: 'Federal posts',
      // Post-cycles worked over post-cycles posted: §4's third production term,
      // averaged over the season rather than sampled at the end of it.
      value: `${ratio(flows.staffedCycles, flows.labourMeteredCycles * labour.posts)} worked`,
      note: `${count(labour.hired)}/${count(labour.posts)} held`,
    });
  }

  const corridor = record.freight;
  if (corridor) {
    // §5's measured freight capacity — *"routes × road quality × congestion"* —
    // as the two counts it is measured from. The card reports both halves and
    // computes no ratio. The third figure is the one a mayor can act on: legs the
    // polity routed at a district with nowhere to put them.
    const turned = shown(flows.legsLapsed) ? ` · ${count(flows.legsLapsed)} turned away` : '';
    lines.push({
      label: 'Corridor',
      value: `${count(flows.legsCompleted)} crossings`,
      note: `${count(flows.legsDispatched)} entered${turned}`,
    });
  }

  return lines;
}

/**
 * What settled — the treasury's season.
 *
 * Money in, money out, then the two levels the district is left standing on.
 * Ordered rather than netted: §8 puts seven new lines in the district budget and
 * a card that showed only their sum would be a card that had done the mayor's
 * reading for them.
 */
function settled(record: SeasonRecord, flows: Flows): ReportLine[] {
  const lines: ReportLine[] = [];

  // Unconditional, and the only row in this block that is: it is the base the
  // tithe is a share of, so a season that collected nothing is a season whose
  // card says so rather than a season with the row missing.
  lines.push({ label: 'Tax collected', value: money(flows.taxCollected), tone: 'credit' });

  pushMoney(lines, 'Rent from the estate', flows.leaseCollected, { tone: 'credit' });
  pushMoney(lines, 'Tolls', flows.tollsEarned, { tone: 'credit' });
  pushMoney(lines, 'Grant received', flows.grantsReceived, { tone: 'credit' });
  // Federal money arriving in the district from outside it, and the only row in
  // this block that does not touch the pool: the treasury sees it a step later,
  // as tax on what those families spend.
  pushMoney(lines, 'Wages paid in', flows.wagesPaid, { note: 'to families' });
  // The other half of the same idea, one layer along: the estate's shopping,
  // placed with the businesses nearest its gate. It does not touch the pool
  // either — by the time the treasury sees it, it is inside `Tax collected` at
  // the top of this block, which is why it is not toned as a credit. A card that
  // added the two would be counting a quarter of this twice.
  pushMoney(lines, 'Orders from the estate', flows.procurementPaid, { note: 'to local businesses' });

  pushMoney(lines, 'UBI paid', flows.ubiDistributed);
  pushMoney(lines, 'Dividends', flows.dividendsDistributed);
  pushMoney(lines, 'Business grants', flows.businessGrantsPaid);
  pushMoney(lines, 'Power and water', flows.utilityBilled);
  if (shown(flows.infraSpending) || shown(flows.infraUnpaid)) {
    lines.push({
      label: 'Road maintenance',
      value: money(flows.infraSpending),
      // §8's death spiral as a number: what the district wanted to spend on its
      // roads and could not. `Roads` three rows down is what it came to.
      note: shown(flows.infraUnpaid) ? `${money(flows.infraUnpaid)} unfunded` : undefined,
    });
  }
  pushMoney(lines, 'Works that pay nothing', flows.worksSpending);
  pushMoney(lines, 'Interest on debt', flows.interestPaid, { tone: 'debit' });
  pushMoney(lines, 'Tithe remitted', flows.tithePaid, { tone: 'debit' });

  lines.push({
    label: 'Treasury',
    value: money(record.treasury.pool),
    note: `${signedMoney(flows.poolChange)} this season`,
    tone: record.treasury.pool < 0 ? 'debit' : 'credit',
  });

  lines.push({
    label: 'Roads',
    value: `${Math.round(record.treasury.roadQuality)}%`,
    // The corridor's other half, in points of road quality rather than in
    // dollars. It is deliberately nowhere near the toll: the two are the two
    // sides of the same bargain and netting them would be taking a position.
    //
    // Tenths, and the threshold matches: a corridor wears half a point per
    // crossing, so a season with one truck in it is `0.5 pts` and not nothing.
    note: flows.roadWear >= 0.05 ? `${flows.roadWear.toFixed(1)} pts worn by transit` : undefined,
  });

  return lines;
}

/**
 * Who moved — §10's one poll a governor cannot spin.
 *
 * > Population is thus the one poll that can't be spun — a governor's true
 * > approval rating is their net migration, and it is public in the Chronicle.
 *
 * So the causes are itemised, by the term that took most off each household's
 * score, and the card draws no conclusion from them. The arrivals are the only
 * derived figure on the whole document: `familyChange` nets the newcomers
 * against the departures, so a district that lost eight families and gained eight
 * reads as flat, and the two halves are separated here by adding the departures
 * back.
 */
function moved(
  record: SeasonRecord,
  previous: SeasonRecord | undefined,
  flows: Flows
): ReportLine[] {
  const lines: ReportLine[] = [];
  const arrived = flows.familyChange + flows.departures;

  lines.push({
    label: 'Families',
    value: count(record.town.families),
    note: `${signedCount(arrived)} in · ${signedCount(-flows.departures)} out`,
  });

  const now = record.welfare?.departuresBy;
  const before = previous?.welfare?.departuresBy;
  if (now) {
    // Walked in the simulation's own order, so two cards about two seasons list
    // the same causes the same way round.
    for (const cause of DEPARTURE_CAUSE_ORDER) {
      const left = (now[cause] ?? 0) - (before?.[cause] ?? 0);
      if (left > 0) {
        lines.push({ label: DEPARTURE_LABELS[cause] ?? cause, value: count(left) });
      }
    }
  }

  const welfare = record.welfare;
  if (welfare) {
    // A mean over the households, so it needs households. A district everybody
    // has left scores 1.00 — the average of nothing — and a card that printed
    // *quality of life 1.00* under *Families 0* would be the one line on this
    // document that was not true.
    if (record.town.families > 0) {
      lines.push({
        label: 'Below the line',
        value: `${count(welfare.strained)} household${welfare.strained === 1 ? '' : 's'}`,
        note: `quality of life ${welfare.qol.toFixed(2)}`,
      });
    }
    if (shown(flows.darkHouseholdCycles)) {
      lines.push({
        label: 'Spent in the dark',
        value: `${count(flows.darkHouseholdCycles)} household-cy`,
      });
    }
    if (shown(flows.dryHouseholdCycles)) {
      lines.push({
        label: 'Spent without water',
        value: `${count(flows.dryHouseholdCycles)} household-cy`,
      });
    }
    if (shown(flows.arrivalsRefused)) {
      // The half of migration a population count cannot show: a district that
      // held steady because nobody left and one that held steady because nobody
      // would come are the same number in the row at the top of this block.
      lines.push({
        label: 'Homes nobody would take',
        value: `${count(flows.arrivalsRefused)} cycles`,
      });
    }
  }

  return lines;
}

/**
 * What's due next — the standing obligations, and nothing forecast.
 *
 * Every row is a **level the record already holds**: a rate somebody set, money
 * already committed, ground already paid for, posts already advertised. Not one
 * of them is a projection, and that is a decision rather than an omission —
 * §12's Ledger Dock is where a mayor is told what a dial is going to cost them,
 * and the Dock is Phase 4's. A card that multiplied this season's receipts by
 * next season's tithe would be inventing a quantity, which is the shape of the
 * thing Law 1 forbids the simulation to do and the interface should not do on
 * its behalf either.
 */
function due(record: SeasonRecord): ReportLine[] {
  const lines: ReportLine[] = [];

  lines.push({
    label: `Season ${count(record.season + 1)}`,
    value: `closes in ${count(CYCLES_PER_SEASON)} cycles`,
  });

  const fiscal = record.fiscal;
  if (fiscal) {
    // The one figure on this card somebody other than the mayor set. Uncoloured,
    // like the status panel's: a dial styled as a threat would be the interface
    // taking the district's side, and it has never taken anybody's.
    if (fiscal.titheRate > 0) {
      lines.push({
        label: 'Tithe',
        value: `${Math.round(fiscal.titheRate * 100)}% of receipts`,
      });
    } else if (fiscal.titheRate < 0) {
      lines.push({
        label: 'Grant',
        value: `${Math.round(-fiscal.titheRate * 100)}% of receipts`,
      });
    }
    pushMoney(lines, 'Arrears', fiscal.owed, { tone: 'debit' });
  }

  const labour = record.labour;
  if (labour) {
    const unfilled = labour.posts - labour.hired;
    if (unfilled > 0) {
      lines.push({
        label: 'Posts unfilled',
        value: `${count(unfilled)} of ${count(labour.posts)}`,
      });
    }
    if (labour.strikes > 0) {
      // The mayor's own standing order, reported back to them. It is the one row
      // in this block that is due because of something the district did.
      lines.push({
        label: 'Staff withdrawn',
        value: `${count(labour.strikes)} facilit${labour.strikes === 1 ? 'y' : 'ies'}`,
      });
    }
  }

  const mercy = record.mercy;
  if (mercy && mercy.saplingCells > 0) {
    lines.push({ label: 'Saplings not yet in', value: `${count(mercy.saplingCells)} cells` });
  }

  const corridor = record.freight;
  if (corridor && corridor.offered > 0) {
    lines.push({
      label: 'Freight offered',
      value: `${count(corridor.offered)} leg${corridor.offered === 1 ? '' : 's'} per cycle`,
    });
  }

  return lines;
}
